/**
 * Hook that orchestrates the full PDF export flow:
 *   1. Capture the 3D viewport snapshot via ViewportService facade
 *   2. Run the DOM → html2canvas → jsPDF pipeline
 *   3. Trigger the browser download
 */

import { useCallback, useState } from 'react';

import { exportReportToPdf } from './pdf-export';

import { useVisualization } from '@/context';
import { useCaseMetadata } from '@/hooks/use-case-metadata';

export interface UsePdfExportOptions {
    viewportId: string;
    fileNamePrefix: string;
}

export function usePdfExport(options: UsePdfExportOptions) {
    const { viewportId, fileNamePrefix } = options;
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { getViewportService } = useVisualization();
    const caseData = useCaseMetadata();

    const exportPdf = useCallback(async () => {
        // caseData is null transiently before the workflow service sets metadata.
        // Abort the export rather than producing a file with no patient identification.
        if (!caseData) {
            setError('Cannot export report: patient data is not yet loaded. Please wait and try again.');
            console.error('[usePdfExport] Cannot export — case metadata not available');
            return;
        }

        setExporting(true);
        setError(null);

        try {
            // 1. Capture the 3D viewport snapshot
            const viewportSnapshotUrl =
                (await getViewportService().captureViewportSnapshot(viewportId, {
                    mimeType: 'image/png',
                })) ?? undefined;

            // 2. Build file name from patient info — sanitize for filesystem safety
            const raw = caseData.lastname ?? caseData.id;
            const sanitized = raw.replace(/[^a-zA-Z0-9_-]/g, '_');
            const fileName = `${fileNamePrefix}_${sanitized}`;

            // 3. Run the export pipeline
            await exportReportToPdf({ viewportSnapshotUrl, fileName });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'PDF export failed';
            console.error('[usePdfExport] Export failed:', err);
            setError(message);
        } finally {
            setExporting(false);
        }
    }, [getViewportService, caseData, viewportId, fileNamePrefix]);

    return { exportPdf, exporting, error };
}
